import React, { useEffect } from 'react';

import {
  adminActions,
  adminDeactivatePharmacist,
  adminHidePharmacist,
  AdminLegendVariant,
  getActivePharmacistList,
  getPharmacistDetails,
  HelpTypes,
  PermissionsOfAdmin,
  PermissionsRemovalKeys,
  resetStatus,
  successSelector,
  TypeOfUser,
} from '@pharmaplan/common';
import { useNavigate } from 'react-router-dom';
import { FormikValues } from 'formik';
import { ICustomBack, renderScreen, resetDrawer } from '../../actions/drawerActions';

import { IPharmacistListActionsParams } from '../../components/Admin/PharmacistList/PharmacistList';
import {
  Constants,
  DeactivateModalIcon,
  OtherScreens,
  ScreenTypes,
} from '../../helpers/Constants';

import strings from '../../localization';
import { setDialog } from '../../reducers/dialogReducer';

import { useAppDispatch } from '../useAppDispatch';
import { useAppSelector } from '../useAppSelector';
import { showSuccess } from '../../components/Admin/Profile/Pharmacist/PharmacistViewProfile/helper';
import DeactivateDialog from '../../components/Admin/PharmacistList/DeactivateDialog/DeactivateDialog';
import useAdminPermissions from '../Admin/useAdminPermissions';
import useDrawer from '../useDrawer';
import {
  setMultipleAddEvents,
  enableAddCalendarEvent,
} from '../../reducers/mainCalendarReducer';

const {
  hidePharmacist: hidePharmacistAction,
  deactivatePharmacist: deactivatePharmacistAction,
} = adminActions;

const { viewRatings, viewTechnicianRating, viewStudentRating } = Constants.paths.admin;

const {
  CreateAvailability,
  CreateMultipleAvailability,
  Edit,
  Deactivate,
  Hide,
} = PermissionsRemovalKeys;

const { createMultiple, createTechnicianMultiple, createStudentMultiple } = Constants.paths.admin;
const professionalsConfig = {
  [HelpTypes.pharmacist]: {
    type: TypeOfUser.pharmacist,
    ratingRoute: viewRatings,
  },
  [HelpTypes.technicians]: {
    type: TypeOfUser.technician,
    ratingRoute: viewTechnicianRating,
  },
  [HelpTypes.students]: {
    type: TypeOfUser.student,
    ratingRoute: viewStudentRating,
  },
};

const usePharmacistList = (userType: HelpTypes) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const hideSuccess = useAppSelector((state) =>
    successSelector([hidePharmacistAction], state));
  const deactivatedSuccess = useAppSelector((state) =>
    successSelector([deactivatePharmacistAction], state));

  const { can } = useAdminPermissions();
  const { openDrawer } = useDrawer();
  const { type, ratingRoute } = professionalsConfig?.[userType as keyof typeof professionalsConfig] ?? HelpTypes.pharmacist;

  const cannotActivate = !can(PermissionsOfAdmin.Activation);
  const cannotPost = !can(PermissionsOfAdmin.Posting);
  const cannotChangeParameters = !can(PermissionsOfAdmin.ChangeParameters);

  const deactivatePharmacist = (values: FormikValues) => {
    const { reason, id } = values ?? {};
    dispatch(adminDeactivatePharmacist({ pharmacistId: id, reason }));
  };

  const showCreateAvailabilityModal = ({ pharmacistId, pharmacistName, back }: IPharmacistListActionsParams) =>
    () => {
      openDrawer();
      dispatch(
        renderScreen({
          screenNumber: 9,
          screenType: ScreenTypes.pharmacistList,
          pharmacistId,
          back,
          extraParameters: {
            name: pharmacistName,
          },
        }),
      );
    };

  const goToShowBookings = (pharmacistId: string, back?: ICustomBack) =>
    () => {
      openDrawer();
      dispatch(
        renderScreen({
          screenNumber: 2,
          screenType: ScreenTypes.pharmacistList,
          pharmacistId,
          back,
        }),
      );
    };

  const goToShowAvailabilities = (pharmacistId: string, back?: ICustomBack) =>
    () => {
      openDrawer();
      dispatch(
        renderScreen({
          screenNumber: 4,
          screenType: ScreenTypes.pharmacistList,
          pharmacistId,
          back,
        }),
      );
    };

  const goToEditPharmacist = (pharmacistId: string, back?: ICustomBack) =>
    () => {
      openDrawer();
      dispatch(getPharmacistDetails(pharmacistId));
      dispatch(
        renderScreen({
          screenNumber: 4,
          screenType: ScreenTypes.profile,
          pharmacistId,
          fromScreen: OtherScreens.PharmacistList,
          type: AdminLegendVariant.posted,
          back,
        }),
      );
    };

  const viewRatingScreen = (pharmacistId: string) =>
    () => {
      dispatch(resetDrawer());
      navigate(ratingRoute, {
        state: { id: pharmacistId, user: type },
      });
    };

  useEffect(() => {
    if (hideSuccess) {
      dispatch(resetStatus([hidePharmacistAction]));
      dispatch(getActivePharmacistList({ page: 1, userType }));
      showSuccess(dispatch, strings.hiddenSuccessfuly);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideSuccess]);

  const hidePharmacist = (values: FormikValues) => {
    const { id, reason } = values ?? {};
    dispatch(adminHidePharmacist({ pharmacistId: id, reason }));
  };

  const showHidePharmacistDialog = (pharmacistId: string) =>
    () => {
      dispatch(
        setDialog({
          Component: (
            <DeactivateDialog
              id={pharmacistId}
              onSubmit={hidePharmacist}
              iconType={DeactivateModalIcon.warning}
              confirmationMessage={strings.areYouSureHideProfessional}
              heading={strings.hideProfessional}
            />
          ),
          showCloseButton: true,
          heading: {
            title: '',
          },
        }),
      );
    };

  const showDeactivateDialog = (pharmacistId: string) =>
    () => {
      dispatch(
        setDialog({
          Component: (
            <DeactivateDialog
              id={pharmacistId}
              onSubmit={deactivatePharmacist}
              iconType={DeactivateModalIcon.warning}
              confirmationMessage={
              strings.formatString(
                strings.areYouSureDeactivateFormat,
                strings.professional,
              ) as string
            }
              heading={strings.deactivateProfessional}
            />
          ),
          showCloseButton: true,
          heading: {
            title: '',
          },
        }),
      );
    };

  const goToListOfPharmacies = (pharmacistId: string, back?: ICustomBack) =>
    () => {
      openDrawer();
      dispatch(
        renderScreen({
          screenNumber: 8,
          screenType: ScreenTypes.pharmacistList,
          pharmacistId,
          back,
        }),
      );
    };

  const goToCreateMultipleAvailabilities = ({ pharmacistId, pharmacistName }: IPharmacistListActionsParams) =>
    () => {
      dispatch(resetDrawer());
      const userMap = {
        [HelpTypes.pharmacist]: createMultiple,
        [HelpTypes.technicians]: createTechnicianMultiple,
        [HelpTypes.students]: createStudentMultiple,
      };

      const path = userMap[userType as keyof typeof userMap];
      dispatch(setMultipleAddEvents(true));
      dispatch(enableAddCalendarEvent(true));
      navigate(`${path}`, {
        state: { id: pharmacistId, name: pharmacistName },
      });
    };

  const showCreateVacation = ({ pharmacistId, pharmacistName, back }: IPharmacistListActionsParams) =>
    () => {
      openDrawer();
      dispatch(
        renderScreen({
          screenNumber: 1,
          screenType: ScreenTypes.createVacation,
          pharmacistId,
          back,
          extraParameters: {
            name: pharmacistName,
          },
        }),
      );
    };

  const actions = (params: IPharmacistListActionsParams) => {
    let arr = [
      {
        key: CreateAvailability,
        onClick: showCreateAvailabilityModal(params),
        label: `${strings.create} ${strings.availability}`,
      },
      {
        key: CreateMultipleAvailability,
        onClick: goToCreateMultipleAvailabilities(params),
        label: strings.createMultipleAvailabilities,
      },
      {
        key: '3',
        onClick: goToShowAvailabilities(params.pharmacistId, params.back),
        label: strings.formatString(strings.view, strings.availabilities),
      },
      {
        key: '13',
        onClick: showCreateVacation(params),
        label: strings.createVacation,
      },
      {
        key: '4',
        onClick: goToShowBookings(params.pharmacistId, params.back),
        label: strings.formatString(strings.view, strings.booking),
      },
      {
        key: '5',
        onClick: goToListOfPharmacies(params.pharmacistId, params.back),
        label: strings.formatString(strings.view, strings.listOfPharmacies),
      },
      {
        key: '6',
        onClick: viewRatingScreen(params.pharmacistId),
        label: strings.viewRating,
      },
      {
        key: Hide,
        onClick: showHidePharmacistDialog(params.pharmacistId),
        label: strings.hide,
      },
      {
        key: Deactivate,
        onClick: showDeactivateDialog(params.pharmacistId),
        label: strings.deactivate,
      },
      {
        key: Edit,
        onClick: goToEditPharmacist(params.pharmacistId, params.back),
        label: strings.editProfileOnly,
      },
    ];

    if (cannotChangeParameters) {
      arr = arr.filter((item) =>
        item.key !== Edit);
    }
    if (cannotActivate) {
      arr = arr.filter(
        (item) =>
          ![Hide, Deactivate].includes(item.key as PermissionsRemovalKeys),
      );
    }
    if (cannotPost) {
      arr = arr.filter(
        (item) =>
          ![CreateAvailability, CreateMultipleAvailability].includes(
            item.key as PermissionsRemovalKeys,
          ),
      );
    }

    return arr;
  };

  useEffect(() => {
    if (deactivatedSuccess) {
      dispatch(resetStatus([deactivatePharmacistAction]));
      dispatch(getActivePharmacistList({ page: 1 }));
      showSuccess(dispatch, strings.deactivatedSuccessfully);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deactivatedSuccess]);

  return { actions };
};

export default usePharmacistList;
